"use client";

import { useEffect, useState } from "react";
import { isVersionSkewError, recoverFromVersionSkew } from "@/lib/version-skew";

/**
 * Frontière d'erreur des pages.
 *
 * Le projet n'en avait aucune : la moindre exception cliente aboutissait à
 * l'écran brut de Next, « This page couldn't load », dont le bouton « Reload »
 * relançait la page depuis le même cache — sans issue quand la cause est un
 * morceau de JavaScript périmé.
 */
export default function Error({
  error,
  unstable_retry,
}: {
  error: Error & { digest?: string };
  unstable_retry: () => void;
}) {
  const [recovering, setRecovering] = useState(false);

  useEffect(() => {
    if (isVersionSkewError(error) && recoverFromVersionSkew()) {
      setRecovering(true);
      return;
    }
    console.error(error);
  }, [error]);

  if (recovering) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center px-6">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Mise à jour de l&apos;application…
        </p>
      </div>
    );
  }

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 px-6 text-center">
      <h2 className="text-lg font-bold text-gray-900 dark:text-white">
        Cette page n&apos;a pas pu s&apos;afficher
      </h2>
      <p className="max-w-sm text-sm text-gray-500 dark:text-gray-400">
        Réessayez dans un instant. Si le problème persiste, rechargez la page.
      </p>
      <div className="mt-1 flex gap-2">
        <button
          onClick={() => unstable_retry()}
          className="rounded-full bg-secondary px-5 py-2.5 text-sm font-bold text-white transition hover:opacity-90 dark:bg-primary dark:text-secondary"
        >
          Réessayer
        </button>
        <button
          onClick={() => window.location.reload()}
          className="rounded-full border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 dark:border-[#313131] dark:text-gray-300 dark:hover:bg-[#262626]"
        >
          Recharger
        </button>
      </div>
      {error.digest && (
        <p className="mt-1 font-mono text-[11px] text-gray-400 dark:text-gray-600">
          {error.digest}
        </p>
      )}
    </div>
  );
}
